import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  User, 
  GraduationCap, 
  Briefcase, 
  Award, 
  FileText, 
  Edit, 
  Plus,
  Eye,
  Download,
  Upload,
  MapPin,
  Mail,
  Phone,
  Calendar,
  ExternalLink,
  Search,
  Filter,
  Clock,
  DollarSign,
  Building,
  LogOut
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const CandidateDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [educations, setEducations] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [resumes, setResumes] = useState([]);
  
  // Job-related state
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Applied jobs state
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [appliedJobsLoading, setAppliedJobsLoading] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchStats();
    fetchProfileData();
    fetchReferenceData();
  }, []);

  // Fetch jobs when Find Jobs tab is active
  useEffect(() => {
    if (activeTab === 'jobs') {
      fetchJobs();
    }
  }, [activeTab, searchTerm, selectedCategory, selectedLocation]);

  // Fetch applied jobs when Applied Jobs tab is active
  useEffect(() => {
    if (activeTab === 'applied') {
      fetchAppliedJobs();
    }
  }, [activeTab]);

  // Refresh data when component becomes visible (user returns from other pages)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchProfileData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/api/auth/me/');
      setProfile(response.data);
    } catch (error) {
      toast.error('Failed to fetch profile');
      console.error('Error fetching profile:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/dashboard/candidate/');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch dashboard statistics');
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfileData = async () => {
    try {
      const [educationsRes, experiencesRes, certificatesRes, resumesRes] = await Promise.all([
        api.get('/api/profile/education/'),
        api.get('/api/profile/experience/'),
        api.get('/api/profile/certificates/'),
        api.get('/api/profile/resumes/')
      ]);

      setEducations(Array.isArray(educationsRes.data) ? educationsRes.data : educationsRes.data?.results || []);
      setExperiences(Array.isArray(experiencesRes.data) ? experiencesRes.data : experiencesRes.data?.results || []);
      setCertificates(Array.isArray(certificatesRes.data) ? certificatesRes.data : certificatesRes.data?.results || []);
      setResumes(Array.isArray(resumesRes.data) ? resumesRes.data : resumesRes.data?.results || []);
    } catch (error) {
      console.error('Error fetching profile data:', error);
      // Set empty arrays as fallback
      setEducations([]);
      setExperiences([]);
      setCertificates([]);
      setResumes([]);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [categoriesRes, locationsRes] = await Promise.all([
        api.get('/api/categories/'),
        api.get('/api/locations/')
      ]);

      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : categoriesRes.data?.results || []);
      setLocations(Array.isArray(locationsRes.data) ? locationsRes.data : locationsRes.data?.results || []);
    } catch (error) {
      console.error('Error fetching reference data:', error);
      setCategories([]);
      setLocations([]);
    }
  };

  const fetchJobs = async () => {
    setJobsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedLocation) params.append('location', selectedLocation);
      params.append('is_active', 'true');

      const response = await api.get(`/api/jobs/?${params.toString()}`);
      setJobs(Array.isArray(response.data) ? response.data : response.data?.results || []);
    } catch (error) {
      toast.error('Failed to fetch jobs');
      console.error('Error fetching jobs:', error);
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  };

  const fetchAppliedJobs = async () => {
    setAppliedJobsLoading(true);
    try {
      const response = await api.get('/api/applications/');
      const applicationsData = Array.isArray(response.data) ? response.data : response.data?.results || [];
      console.log('Applied jobs data:', applicationsData);
      setAppliedJobs(applicationsData);
    } catch (error) {
      toast.error('Failed to fetch applied jobs');
      console.error('Error fetching applied jobs:', error);
      setAppliedJobs([]);
    } finally {
      setAppliedJobsLoading(false);
    }
  };

  const handleApplyJob = async (jobId) => {
    try {
      // Check if user has resumes
      if (resumes.length === 0) {
        toast.error('Please upload a resume before applying to jobs');
        return;
      }

      // For now, use the primary resume or first resume
      const primaryResume = resumes.find(r => r.is_primary) || resumes[0];
      
      console.log('Applying to job:', jobId);
      console.log('User ID:', user.id);
      console.log('Resume ID:', primaryResume.id);
      
      const applicationData = {
        job: parseInt(jobId),
        candidate: user.id,
        resume: primaryResume.id,
        cover_letter: `I am interested in this position and would like to apply.`
      };
      
      console.log('Application data:', applicationData);
      
      await api.post('/api/applications/', applicationData);

      toast.success('Application submitted successfully!');
      
      // Refresh applied jobs if the applied tab is active
      if (activeTab === 'applied') {
        fetchAppliedJobs();
      }
    } catch (error) {
      toast.error('Failed to submit application');
      console.error('Error applying to job:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Candidate Dashboard</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Welcome back, {user?.first_name}!
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Link
                to="/resume-builder"
                className="btn-primary flex items-center space-x-2 bg-green-600 hover:bg-green-700"
              >
                <FileText className="w-5 h-5" />
                <span>Build Resume</span>
              </Link>
              <Link
                to="/ai-recommendations"
                className="btn-primary flex items-center space-x-2 bg-blue-600 hover:bg-blue-700"
              >
                <FileText className="w-5 h-5" />
                <span>AI Recommendations</span>
              </Link>
              <Link
                to="/resume-analyzer"
                className="btn-primary flex items-center space-x-2 bg-blue-600 hover:bg-blue-700"
              >
                <FileText className="w-5 h-5" />
                <span>AI Analysis</span>
              </Link>

              {/* <Link
                to="/profile/edit"
                className="btn-primary flex items-center space-x-2"
              >
                <Edit className="w-5 h-5" />
                <span>Edit Profile</span>
              </Link> */}
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors duration-200"
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'profile'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <User className="w-4 h-4 inline mr-2" />
                Profile
              </button>
              <button
                onClick={() => setActiveTab('jobs')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'jobs'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Briefcase className="w-4 h-4 inline mr-2" />
                Find Jobs
              </button>
              <button
                onClick={() => setActiveTab('applied')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'applied'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Applied Jobs
              </button>
            </nav>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Tab Content */}
        {activeTab === 'profile' && (
          <>
            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Applications</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.applications.total}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Eye className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Pending</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.applications.pending}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Award className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Shortlisted</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.applications.shortlisted}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <FileText className="w-6 h-6 text-orange-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Rejected</p>
                      <p className="text-2xl font-bold text-gray-900">0</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Profile Overview */}
            <div className="bg-white shadow rounded-lg mb-8">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Profile Overview</h2>
              </div>
              <div className="px-6 py-4">
                <div className="flex items-center space-x-6">
                  <div className="flex-shrink-0">
                    {profile?.profile_picture ? (
                      <img
                        className="h-16 w-16 rounded-full object-cover"
                        src={profile.profile_picture}
                        alt="Profile"
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-full bg-gray-300 flex items-center justify-center">
                        <User className="h-8 w-8 text-gray-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">
                      {profile?.first_name} {profile?.last_name}
                    </h3>
                    <p className="text-sm text-gray-600">{profile?.job_title}</p>
                    <p className="text-sm text-gray-600">{profile?.current_company}</p>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                      {profile?.location && (
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          {profile.location}
                        </div>
                      )}
                      {profile?.email && (
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 mr-1" />
                          {profile.email}
                        </div>
                      )}
                      {profile?.phone && (
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 mr-1" />
                          {profile.phone}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Sections Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Education Section */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">Education</h2>
                    <Link
                      to="/profile/education"
                      className="text-primary-600 hover:text-primary-500 text-sm font-medium flex items-center"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Manage Education
                    </Link>
                  </div>
                </div>
                <div className="px-6 py-4">
                  {educations.length === 0 ? (
                    <div className="text-center py-8">
                      <GraduationCap className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No education added</h3>
                      <p className="mt-1 text-sm text-gray-500">Add your educational background to showcase your qualifications.</p>
                      <div className="mt-6">
                        <Link
                          to="/profile/education"
                          className="btn-primary"
                        >
                          Add Education
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {educations.slice(0, 3).map((education) => (
                        <div key={education.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <GraduationCap className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-sm font-medium text-gray-900">
                                {education.degree} in {education.field_of_study}
                              </h3>
                              <p className="text-sm text-gray-600">{education.institution}</p>
                              <p className="text-xs text-gray-500">
                                {education.start_date} - {education.is_current ? 'Present' : education.end_date}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                      {educations.length > 3 && (
                        <div className="text-center pt-2">
                          <Link
                            to="/profile/education"
                            className="text-sm text-primary-600 hover:text-primary-500"
                          >
                            View all {educations.length} education records
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Experience Section */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">Work Experience</h2>
                    <Link
                      to="/profile/experience"
                      className="text-primary-600 hover:text-primary-500 text-sm font-medium flex items-center"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Manage Experience
                    </Link>
                  </div>
                </div>
                <div className="px-6 py-4">
                  {experiences.length === 0 ? (
                    <div className="text-center py-8">
                      <Briefcase className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No experience added</h3>
                      <p className="mt-1 text-sm text-gray-500">Add your work experience to highlight your professional background.</p>
                      <div className="mt-6">
                        <Link
                          to="/profile/experience"
                          className="btn-primary"
                        >
                          Add Experience
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {experiences.slice(0, 3).map((experience) => (
                        <div key={experience.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                              <Briefcase className="w-5 h-5 text-green-600" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-sm font-medium text-gray-900">
                                {experience.position}
                              </h3>
                              <p className="text-sm text-gray-600">{experience.company}</p>
                              <p className="text-xs text-gray-500">
                                {experience.start_date} - {experience.is_current ? 'Present' : experience.end_date}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                      {experiences.length > 3 && (
                        <div className="text-center pt-2">
                          <Link
                            to="/profile/experience"
                            className="text-sm text-primary-600 hover:text-primary-500"
                          >
                            View all {experiences.length} experience records
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Certificates Section */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">Certificates</h2>
                    <Link
                      to="/profile/certificates"
                      className="text-primary-600 hover:text-primary-500 text-sm font-medium flex items-center"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Manage Certificates
                    </Link>
                  </div>
                </div>
                <div className="px-6 py-4">
                  {certificates.length === 0 ? (
                    <div className="text-center py-8">
                      <Award className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No certificates added</h3>
                      <p className="mt-1 text-sm text-gray-500">Add your certificates and certifications to showcase your skills.</p>
                      <div className="mt-6">
                        <Link
                          to="/profile/certificates"
                          className="btn-primary"
                        >
                          Add Certificate
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {certificates.slice(0, 3).map((certificate) => (
                        <div key={certificate.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-purple-100 rounded-lg">
                              <Award className="w-5 h-5 text-purple-600" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-sm font-medium text-gray-900">
                                {certificate.name}
                              </h3>
                              <p className="text-sm text-gray-600">{certificate.issuing_organization}</p>
                              <p className="text-xs text-gray-500">
                                {certificate.issue_date} - {certificate.is_permanent ? 'No Expiry' : certificate.expiry_date}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                      {certificates.length > 3 && (
                        <div className="text-center pt-2">
                          <Link
                            to="/profile/certificates"
                            className="text-sm text-primary-600 hover:text-primary-500"
                          >
                            View all {certificates.length} certificates
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Resume Section */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">Resume</h2>
                    <Link
                      to="/profile/resume"
                      className="text-primary-600 hover:text-primary-500 text-sm font-medium flex items-center"
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      Manage Resume
                    </Link>
                  </div>
                </div>
                <div className="px-6 py-4">
                  {resumes.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No resume uploaded</h3>
                      <p className="mt-1 text-sm text-gray-500">Upload your resume to apply for jobs easily.</p>
                      <div className="mt-6">
                        <Link
                          to="/profile/resume"
                          className="btn-primary"
                        >
                          Upload Resume
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {resumes.slice(0, 3).map((resume) => (
                        <div key={resume.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <FileText className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <h3 className="text-sm font-medium text-gray-900">
                                  {resume.name}
                                </h3>
                                {resume.is_primary && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Primary
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500">
                                {resume.file_type.toUpperCase()} • {Math.round(resume.file_size / 1024)} KB
                              </p>
                              <p className="text-xs text-gray-500">
                                Uploaded {new Date(resume.uploaded_at).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <a
                                href={resume.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                title="View resume"
                              >
                                <Eye className="w-4 h-4" />
                              </a>
                              <a
                                href={resume.file_url}
                                download
                                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                title="Download resume"
                              >
                                <Download className="w-4 h-4" />
                              </a>
                            </div>
                          </div>
                        </div>
                      ))}
                      {resumes.length > 3 && (
                        <div className="text-center pt-2">
                          <Link
                            to="/profile/resume"
                            className="text-sm text-primary-600 hover:text-primary-500"
                          >
                            View all {resumes.length} resumes
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Jobs Tab Content */}
        {activeTab === 'jobs' && (
          <div className="space-y-6">
            {/* Search and Filter Bar */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search jobs by title, company, or keywords..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">All Categories</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">All Locations</option>
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Jobs List */}
            <div className="space-y-4">
              {jobsLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : jobs.length === 0 ? (
                <div className="text-center py-12">
                  <Briefcase className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchTerm || selectedCategory || selectedLocation
                      ? 'Try adjusting your search criteria.'
                      : 'No active job postings available at the moment.'}
                  </p>
                </div>
              ) : (
                jobs.map((job) => (
                  <div key={job.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                    <div className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {job.title}
                            </h3>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {job.job_type}
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                            <div className="flex items-center">
                              <Building className="w-4 h-4 mr-1" />
                              {job.company_name}
                            </div>
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 mr-1" />
                              {job.location}
                            </div>
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 mr-1" />
                              {job.employment_type}
                            </div>
                            {job.salary_min && (
                              <div className="flex items-center">
                                <DollarSign className="w-4 h-4 mr-1" />
                                ${job.salary_min.toLocaleString()}
                                {job.salary_max && ` - $${job.salary_max.toLocaleString()}`}
                              </div>
                            )}
                          </div>

                          <p className="text-gray-700 text-sm mb-4 line-clamp-2">
                            {job.description}
                          </p>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <span>Posted {new Date(job.created_at).toLocaleDateString()}</span>
                              <span>•</span>
                              <span>{job.category_name}</span>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <Link
                                to={`/jobs/${job.id}`}
                                className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-500 border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                              >
                                View Details
                              </Link>
                              <button
                                onClick={() => handleApplyJob(job.id)}
                                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                              >
                                Apply Now
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Applied Jobs Tab Content */}
        {activeTab === 'applied' && (
          <div className="space-y-6">
            {/* Applied Jobs Header */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Applied Jobs</h2>
                  <p className="mt-1 text-sm text-gray-600">
                    Track the status of your job applications
                  </p>
                </div>
                <div className="text-sm text-gray-500">
                  {appliedJobs.length} application{appliedJobs.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>

            {/* Applied Jobs List */}
            <div className="space-y-4">
              {appliedJobsLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : appliedJobs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No applications yet</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    You haven't applied to any jobs yet. Start by browsing available positions.
                  </p>
                  <div className="mt-6">
                    <button
                      onClick={() => setActiveTab('jobs')}
                      className="btn-primary"
                    >
                      Browse Jobs
                    </button>
                  </div>
                </div>
              ) : (
                    appliedJobs.map((application) => {
                      console.log('Application object:', application);
                      return (
                      <div key={application.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                    <div className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {application.job_title || application.job?.title || 'Job Title Not Available'}
                            </h3>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              application.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              application.status === 'reviewed' ? 'bg-blue-100 text-blue-800' :
                              application.status === 'shortlisted' ? 'bg-green-100 text-green-800' :
                              application.status === 'interviewed' ? 'bg-purple-100 text-purple-800' :
                              application.status === 'accepted' ? 'bg-green-100 text-green-800' :
                              application.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {application.status?.charAt(0).toUpperCase() + application.status?.slice(1) || 'Unknown'}
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                            <div className="flex items-center">
                              <Building className="w-4 h-4 mr-1" />
                              {application.job_company_name || application.job?.company_name || 'Company Not Available'}
                            </div>
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 mr-1" />
                              {application.job_location || application.job?.location || 'Location Not Available'}
                            </div>
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 mr-1" />
                              Applied {new Date(application.applied_at).toLocaleDateString()}
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <span>Application ID: #{application.id}</span>
                              {(application.job_category_name || application.job?.category_name) && (
                                <>
                                  <span>•</span>
                                  <span>{application.job_category_name || application.job?.category_name}</span>
                                </>
                              )}
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              {(application.job && application.job.id) || application.job_id ? (
                                <Link
                                  to={`/jobs/${application.job?.id || application.job_id}`}
                                  className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-500 border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                                >
                                  View Job
                                </Link>
                              ) : (
                                <span className="px-4 py-2 text-sm text-gray-400 border border-gray-300 rounded-lg cursor-not-allowed">
                                  Job Unavailable
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                      );
                    })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateDashboard;